import React from 'react';
import Interweave from 'interweave';
import { Carousel } from 'react-responsive-carousel';
import "react-responsive-carousel/lib/styles/carousel.min.css"; // requires a loader

import { Box, Button, Typography, LinearProgress } from '@mui/material';

import BlockFactory from './BlockFactory';
import BlockEditHeader from './BlockEditHeader';
import BlockEditFooter from './BlockEditFooter';

import ColorPallet from './ColorPallet';
import ScaleBar from './ScaleBar';
import NoImageIcon from 'icons/drawable/ic_img_empty.svg';
import DownloadIcon from 'icons/drawable/ic_download.svg';
import "./PDFBlock.css";

function PdfBlockEditView({idx, iconChar, iconColor, block, onUpdate, onDelete, onDone, onCancel}) {

	const pdfAreaSize = React.useRef({ width: 0, height: 0 });

	const [data,setData] = React.useState(block.data ? block.data : '');
	const [resources,setResources] = React.useState(block.resources);
	const [namedUrls, setNamedUrls] = React.useState([]);
	const [progress, setProgress] = React.useState(0);
	const [transcoding, setTranscoding] = React.useState(0)

	const [uploadProgress,setUploadProgress] = React.useState(-1);

	const [optionMode,setOptionMode] = React.useState();
	const [backgroundColor,setBackgroundColor] = React.useState(block.backgroundColor);
	const [scale,setScale] = React.useState(block.scale === undefined ? 1 : block.scale);
	const [translationX,setTranslationX] = React.useState(block.translationX === undefined ? 0 : block.translationX);

	function getPdfAreaName() {
		return 'pdf-area-' + block.name;
	}

	function getSelectPdfInputName() {
		return 'select-pdf-' + block.name;
	}

	React.useEffect(() => {
		let pdfArea = document.getElementById(getPdfAreaName());
		if (pdfArea) {
			console.log("pdfAreaWidth = " + pdfArea.clientWidth +
				", pdfAreaHeight = " + pdfArea.clientHeight);

			pdfAreaSize.current = {
				width: pdfArea.clientWidth,
				height: pdfArea.clientHeight
			}
		}
	});

	React.useEffect(()=> {
		if (block.data) {
			_checkChangedPdf([block.data], _setProgress);
		}
	}, []);

	const getUpdatedBlock = (b, s, t, d, r) => {
		let updatedBlock = BlockFactory.clone(block);
		updatedBlock.backgroundColor = b;
		updatedBlock.scale = s;
		updatedBlock.translationX = t;
		updatedBlock.data = d;
		updatedBlock.resources = r;
		return updatedBlock;
	};

	const handlePdfSelect = async (e) => {
		let f = e.target.files[0];
		if (!f) return;
		
		if (!f.type.startsWith('application/pdf')	) {
			alert("PDF 파일이 아닙니다.\nPDF 파일을 선택해 주세요.");
			return;
		}

		if (f) {
			const RESOLUTION_LIMIT = 600;

			setUploadProgress(0);
			let uploadResult = await global.dipClient.uploadFile('pdf', f, RESOLUTION_LIMIT,
				(p) => { setUploadProgress(p); });

			setUploadProgress(1);

			if	(uploadResult && uploadResult.resourceId) {
				// It seems Interweave always adds small padding to the bottom, margin-bottom of -5px here
				// is to fix it.
				// `<img src="${uploadResult.url}" style="top:0;left:0;width:100%;margin-bottom:-5px;object-fit:contain;" alt="image block"/>`,
				const resourceIds = [uploadResult.resourceId];
				console.log('handlePdfSelect / resourceIds = ', resourceIds, );
				// let data = `<embed src="${pdfUrl}" type="application/pdf" width="100%" height="500px" style="top:0;left:0;width:100%;margin-bottom:-5px;object-fit:contain" />`;
				handlePdfChange(
					uploadResult.resourceId,
					resourceIds,
				);
				setUploadProgress(-1);
				_checkChangedPdf(resourceIds, _setProgress);
				return;
			}
			setUploadProgress(-1);
		}
	};
	const _setProgress = (prg) => {
		setProgress(prg);
	}

	const _checkChangedPdf = async (resourceIds, progressCallback) => {
		setTranscoding(1);
		console.log('[PdfBlockEditView] _checkChangedPdf / resourceIds = ', resourceIds);
    let resources = await global.dipClient.waitUntilResourceReady2(
      resourceIds,
      progressCallback,
    );
		console.log('[PdfBlockEditView] _checkChangedPdf resources = ', resources);
    if (resources && resources.length > 0) {
			setNamedUrls(resources[0].namedUrls);
    	setTranscoding(0);
			return;
    }
		setTranscoding(-1);
	}

	const handleBackgroundColorChange = (v) => {
		let newBackgroundColor = v;
		setBackgroundColor(newBackgroundColor);
		if (onUpdate) {
			onUpdate(getUpdatedBlock(newBackgroundColor, scale, translationX, data, resources));
		}
	};

	const handleScaleChange = (v) => {
		let newScale = v / 100;
		setScale(newScale);
		if (onUpdate) {
			onUpdate(getUpdatedBlock(backgroundColor, newScale, translationX, data, resources));
		}
	};

	const handleTranslationXChange = (v) => {
		let newTranslationX = (v - 50) * 2;
		setTranslationX(newTranslationX);
		if (onUpdate) {
			onUpdate(getUpdatedBlock(backgroundColor, scale, newTranslationX, data, resources));
		}
	};

	const handlePdfChange = (newData, newResources) => {
		setData(newData);
		if (onUpdate) {
			onUpdate(getUpdatedBlock(backgroundColor, scale, translationX, newData, newResources));
		}
	};

	const handleDelete = () => {
		if (onDelete) {
			onDelete(block);
		}
	};

	const handleDone = () => {
		if (onDone) {
			onDone(getUpdatedBlock(backgroundColor, scale, translationX, data, resources));
		}
	};

	const handleCancel = () => {
		if (onCancel) {
			onCancel(block);
		}
	};

	const getPdfArea = () => {
		if (uploadProgress !== -1) {
			return (
				<Box
					id={getPdfAreaName()}
					display='flex'
					justifyContent='center'
					alignItems='center'
					sx={{
						width: '100%',
						height: `${140/360 * 100}vw`,
						backgroundColor:'black'
					}}
				>
					<Box
						display='flex'
						flexDirection='column'
						alignItems='center'
					>
						<Typography variant="body1" color="textSecondary">
							{'Uploading'}
						</Typography>
						<Typography variant="body2" color="textSecondary">
							{`${Math.round(uploadProgress * 100)}%`}
						</Typography>
					</Box>
				</Box>
			);
		}

		if (transcoding === 1) {
      // 트랜스 코딩중
      return (
        <Box
          id={`${getPdfAreaName()}-transcoding`}
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{
            width: "100%",
            height: `${(140 / 360) * 100}vw`,
            backgroundColor: "black",
          }}
        >
          <Box display="flex" flexDirection="column" alignItems="center">
            <Typography variant="body1" color="textSecondary">
              {"PDF을 변환 중입니다"}
            </Typography>
            <LinearProgress sx={{width: '100%', marginTop: '16px'}}  color="secondary" />
          </Box>
        </Box>
      );
    }

		if (!data) {
			return (
				<div
					id={getPdfAreaName()}
					style={{
						width: '100%',
						height: `${140/360 * 100}vw`,
						borderRadius: `${5.6/360 * 100}vw`,
						backgroundColor: '#f2f2f2',
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
						alignItems: 'center',
					}}
				>
					<img src={NoImageIcon} alt="not uploaded icon"/>
					<Typography
						fontFamily='노토산스 Regular'
						fontSize={`${14/360 * 100}vw`}
						color='#aeaeae'
						sx={{
							marginTop: `${10/360 * 100}vw`
						}}
					>
						PDF를 업로드하세요
					</Typography>
				</div>
			);
		}

		const style = {
			width: '100%',
			transform: `translateX(${translationX}%) scale(${scale})`,
		};

		return (
			<div
				id={getPdfAreaName()}
				style={{
					width:'100%',
					//border:'2px solid red',
					backgroundColor: backgroundColor,
					overflow: 'hidden',
				}}
			>
				<div id={block.name} style={style}>
					{/* <Box display="flex" flexDirection="column" alignItems="center"> */}
						{/* <Interweave content={data} /> */}
						{renderCarousel()}
						{renderDownloadButton()}
					{/* </Box> */}
				</div>

			</div>
		);
	}
	const handleDownload = async (e) => {
		console.log('handleDownload / url = ', namedUrls);
		const link = document.createElement("a");
    link.href = namedUrls.download[0];
    link.click();
	}
	const renderDownloadButton = () => {
		return (
			<Button sx={{
				position:'absolute',
				left: '0px',
				top: '0px',
				width: '30px',
				height: '30px'
			}}
			onClick={handleDownload}
			>
				<img src={DownloadIcon} alt='download icon' style={{
					width: '30px',
					height: '30px',
					objectFit:'contain'}} />
			</Button>
		);
	}

	const getButtons = () => {
		const btnFontSize = 14;
		const btnTextMargin = -10;

		return (
			<Box
				display='flex'
				flexDirection='row'
				justifyContent='center'
				alignItems='center'
				sx={{
					width:'100%',
					marginTop: `${20/360 * 100}vw`,
				}}
			>
				<Button
					variant="outlined"
					sx={{
						bgcolor: optionMode === 'color' ? '#336cadff' : 'white',
						border: `${1/360 * 100}vw solid #6cadff`,
						borderRadius: `${15/360 * 100}vw`,
						width: `${74/360 * 100}vw`,
						height: `${30/360 * 100}vw`,
						marginRight: `${8/360 * 100}vw`,
					}}
					onClick={e => setOptionMode(optionMode === 'color' ? undefined : 'color')}
				>
					<Typography
						fontFamily='노토산스 Bold'
						fontSize={`${btnFontSize/360 * 100}vw`}
						color={optionMode === 'color' ? 'white' : '#6cadff'}
						sx={{margin:`${btnTextMargin}px`}}
					>
						배경색상
					</Typography>
				</Button>
				
				<input
					id={getSelectPdfInputName()}
					type='file'
					accept='application/pdf'
					hidden
					onChange={handlePdfSelect}
				/>
				<label htmlFor={getSelectPdfInputName()}>
					<Button
						variant="contained"
						sx={{
							bgcolor: "#6cadff",
							border: `${1/360 * 100}vw solid #6cadff`,
							borderRadius: `${15/360 * 100}vw`,
							width: `${74/360 * 100}vw`,
							height: `${30/360 * 100}vw`,
						}}
						onClick={e => {
							setOptionMode(undefined);
							document.getElementById(getSelectPdfInputName()).click()
						}}
					>
						<Typography
							fontFamily='노토산스 Bold'
							fontSize={`${btnFontSize/360 * 100}vw`}
							color='white'
							sx={{margin:`${btnTextMargin}px`}}
						>
							업로드
						</Typography>
					</Button>
				</label>
			</Box>
		);
	};

	const getOptionalSetting = () => {
		if (optionMode === 'color') {
			return (
				<ColorPallet curColorValue={backgroundColor} onUpdate={handleBackgroundColorChange}/>
			);
		}
		else if (optionMode === 'scale') {
			return (
				<ScaleBar value={scale * 100} onUpdate={handleScaleChange}/>
			);
		}
		else if (optionMode === 'translationX') {
			return (
				<ScaleBar value={(translationX / 2) + 50} onUpdate={handleTranslationXChange}/>
			);
		}
	};
	const renderCarousel = () => {
		return (
			<Carousel
			className={'carousel'}
			// transitionTime={800}
			infiniteLoop={false}
			showIndicators={false}
			showThumbs={false}
			showArrows={false}
			autoPlay={false}
			showStatus={false}
			axis={'horizontal'}
			dynamicHeight={false}
			swipeable={true}
			emulateTouch={true}
			// renderIndicator={(onClickHandler, isSelected, index, label) => {
			// 	return (
			// 		<div
			// 			style={{
			// 				// width: '6px',
			// 				// height: '6px',
			// 				// borderRadius: '6px',
			// 				// backgroundColor: isSelected ? 'rgb(101, 101, 101)' : 'rgb(184, 184,184)',
			// 				// marginBottom: '0px',
			// 				// // marginTop: '12px',
			// 				// marginInline: '3px',
			// 				// display: 'inline-block',
			// 			}}
			// 		/>
			// 	);
			// }}
			// centerMode={true}
		>
			{namedUrls?.image?.map((obj, index) => {
				return (
					<img
						key={`carousel-index-${index}`}
						alt={'images'}
						src={obj}
						width={'100%'}
						height={'250px'}
						style={{
							objectFit: 'contain',
						}}
					/>
				);
			})}
		</Carousel>
		);
	}

	return (
		<Box
			display="flex"
			flexDirection="column"
			justifyContent="center"
			alignItems="center"
			sx={{
				left:`${20/360 * 100}vw`,
				width:`${320/360 * 100}vw`,
			}}
		>
			<BlockEditHeader
				blockName={block.name}
				iconChar={iconChar}
				iconColor={iconColor}
				onDelete={onDelete ? () => handleDelete() : undefined}
			/>
			{getPdfArea()}
			{getButtons()}
			{getOptionalSetting()}
			{
				onDone && onCancel &&
				<BlockEditFooter
					onDone={e => handleDone()}
					onCancel={e => handleCancel()}
				/>
			}
		</Box>
	);
}

export default PdfBlockEditView;
